import React, { useState } from 'react';
import { connect } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import startCase from 'lodash/startCase';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import Image from 'components/Image';
import DateTimePicker from 'components/DateTimePicker';
import Select from 'components/Forms/Fields/Select';
import DetailField from 'components/Details';
import { assetCategoryOptions as qualificationCategories } from 'utils/constants/assetText';
import { images } from 'utils/icons';
import { checkProperties } from 'utils/valueChecker';
import styles from './index.module.scss';
import { assetTypeSelectors } from 'containers/Pages/Assets/AssetTypes';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  },
  input: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: 1,
    padding: '1rem 0',
  },
  values: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  value: {
    width: '18%',
  },
}));

const CustomSelect = ({
  required,
  label,
  selectedTypes,
  value = [],
  defaultValues,
  onChange,
  rawAssets,
}) => {
  const [assets, updateAssets] = useState([]);
  const [types, setTypes] = useState({});
  const [values, setValues] = useState(defaultValues);
  const [touched, setTouched] = useState({});

  const handleChange = evt => {
    const key = evt.target.name;
    const { value } = evt.target;
    if (key === 'category') {
      let filteredAssets;
      if (value === 'ALL') {
        filteredAssets = rawAssets;
      } else {
        filteredAssets = rawAssets.filter(asset => asset.category === value);
      }

      updateAssets(filteredAssets);
    }

    if (key === 'id') {
      const foundAsset = find(rawAssets, { id: value });
      setValues({
        ...values,
        [key]: value,
        typeName: foundAsset.name,
        category: foundAsset.category,
      });
      setTypes({ ...types, typeName: 'text', [key]: 'text' });
    } else {
      const item = find(selectedTypes, ['field', key]);

      setValues({
        ...values,
        [key]: value,
      });
      setTypes({ ...types, [key]: item.type });
    }
  };

  const setFieldValue = (key, val) => {
    const { type } = find(selectedTypes, ['field', key]);
    setTypes({ ...types, [key]: type });

    setValues({
      ...values,
      [key]: val,
    });
  };
  const setFieldTouched = key => setTouched({ ...touched, [key]: true });
  const handleBtnClick = () => {
    const addedValues = selectedTypes.reduce((acc, item) => {
      return {
        ...acc,
        [item.field]: {
          value: values[item.field],
          type: types[item.field],
        },
      };
    }, {});
    onChange([...value, addedValues]);
    setTouched({});
    setValues(defaultValues);
    setTypes({});
  };

  const removeAddedItem = text => {
    const remainingItems = value.filter(item => item !== text);
    onChange(remainingItems);
  };

  const classes = useStyles();

  return (
    <div className={classes.container}>
      <InputLabel shrink required={required}>
        {label}
      </InputLabel>
      <div className={classes.input}>
        <Select
          name={'category'}
          label={startCase('category')}
          className={cn(styles.textInput, styles['quarter'])}
          required={true}
          options={qualificationCategories}
          value={values['category']}
          onChange={handleChange}
          onFocus={() => setFieldTouched('category')}
        />
        <Select
          name={'id'}
          label={startCase('typeName')}
          className={cn(styles.textInput, styles['quarter'])}
          required={true}
          options={assets}
          value={values['id']}
          onChange={handleChange}
          onFocus={() => setFieldTouched('id')}
        />

        <DateTimePicker
          className={cn(styles.textInput, styles['quarter'])}
          onChange={value => {
            setFieldValue('validFrom', value);
          }}
          required={true}
          label={startCase('validFrom')}
          value={values['validFrom']}
        />
        <DateTimePicker
          className={cn(styles.textInput, styles['quarter'])}
          onChange={value => {
            setFieldValue('validTo', value);
          }}
          required={true}
          label={startCase('validTo')}
          minDate={values['validFrom']}
          value={values['validTo']}
        />

        {checkProperties(values) && (
          <Image src={images.addMedia.default} onClick={handleBtnClick} />
        )}
      </div>
      {value.length > 0 && (
        <section>
          <section>
            <InputLabel shrink required={required}>
              {`Added ${label}`}
            </InputLabel>
            {value.map((items, itemIndex) => (
              <div className={classes.values} key={itemIndex}>
                {Object.keys(values).map((item, index) => {
                  return (
                    <div className={classes.value} key={index}>
                      <DetailField
                        type={items[item].type}
                        value={items[item].value}
                      />
                    </div>
                  );
                })}
                <IconButton
                  color="secondary"
                  component="div"
                  className={classes.icon}
                  onClick={() => removeAddedItem(items)}
                >
                  <DeleteOutlineIcon color="disabled" />
                </IconButton>
              </div>
            ))}
          </section>
        </section>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  required: PropTypes.bool,
  label: PropTypes.string,
  selectedTypes: PropTypes.arrayOf(PropTypes.object),
  value: PropTypes.array,
  defaultValues: PropTypes.object,
  onChange: PropTypes.func,
  rawAssets: PropTypes.array,
};

const mapStateToProps = state => ({
  rawAssets: assetTypeSelectors.getAssetTypeListTransformed(state),
});

export default connect(mapStateToProps)(CustomSelect);
